import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Card,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { WorkOrderTypeBadge, WorkOrderStatusBadge, WorkOrderPriorityBadge } from "./WorkOrderBadges";
import type { WorkOrder } from "../types";
import { formatDistanceToNow } from "date-fns";

interface WorkOrdersTableProps {
  workOrders: WorkOrder[];
  orderBy: string;
  order: "asc" | "desc";
  onSort: (property: string) => void;
}

const columns = [
  { id: "id", label: "W/O", width: 110 },
  { id: "priority", label: "Priority", width: 90 },
  { id: "type", label: "Type", width: 110 },
  { id: "status", label: "Status", width: 120 },
  { id: "location", label: "Location", width: "auto" },
  { id: "assignedTechnician", label: "Technician", width: 140 },
  { id: "zone", label: "Zone", width: 60 },
  { id: "createdAt", label: "Created", width: 100 },
  { id: "sla", label: "SLA", width: 50 },
  { id: "actions", label: "", width: 50 },
];

export const WorkOrdersTable = ({ workOrders, orderBy, order, onSort }: WorkOrdersTableProps) => {
  const navigate = useNavigate();

  const handleRowClick = (workOrderId: string) => {
    navigate(`/work-orders/${workOrderId}`);
  };

  if (workOrders.length === 0) {
    return (
      <Card variant="outlined" sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No work orders found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Try adjusting your filters to see more results
        </Typography>
      </Card>
    );
  }

  return (
    <TableContainer component={Card} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id} sx={{ width: column.width }}>
                {column.id !== "actions" && column.id !== "sla" ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => onSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {workOrders.map((wo) => (
            <TableRow
              key={wo.id}
              hover
              onClick={() => handleRowClick(wo.id)}
              sx={{
                cursor: "pointer",
                bgcolor: wo.slaBreached ? "error.50" : undefined,
              }}
            >
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 11 }}>
                  {wo.id}
                </Typography>
              </TableCell>
              <TableCell>
                <WorkOrderPriorityBadge priority={wo.priority} />
              </TableCell>
              <TableCell>
                <WorkOrderTypeBadge type={wo.type} />
              </TableCell>
              <TableCell>
                <WorkOrderStatusBadge status={wo.status} />
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                    {wo.location}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                {wo.assignedTechnician ? (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>
                      {wo.assignedTechnician.name.charAt(0)}
                    </Avatar>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                      {wo.assignedTechnician.name}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">Unassigned</Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip label={wo.zone} size="small" variant="outlined" sx={{ fontSize: 10 }} />
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <ScheduleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 11 }}>
                    {formatDistanceToNow(new Date(wo.createdAt), { addSuffix: true })}
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell>
                {wo.slaBreached && (
                  <Tooltip title="SLA Breached">
                    <WarningIcon fontSize="small" color="error" />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(wo.id);
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
